// src/context/AuthContext.js
import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { API } from "../utils/constants";

const AuthContext = createContext(null);

const REFRESH_INTERVAL_MS = 14 * 60 * 1000;

export function AuthProvider({ children }) {
  const [authUser, setAuthUser]       = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading]         = useState(true);
  const refreshTimer = useRef(null);

  const clearSession = useCallback(() => {
    setAuthUser(null);
    setAccessToken(null);
    if (refreshTimer.current) clearInterval(refreshTimer.current);
  }, []);

  // Fetch full profile from /me and merge into authUser (gets minecraft_username etc.)
  const fetchProfile = useCallback(async (token) => {
    try {
      const r = await fetch(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!r.ok) return;
      const d = await r.json();
      if (d?.user) setAuthUser(d.user);
    } catch { /* non-fatal */ }
  }, []);

  const applyToken = useCallback((token) => {
    setAccessToken(token);
    // Decode JWT for immediate minimal user info, then fetch full profile
    const payload = JSON.parse(atob(token.split(".")[1]));
    setAuthUser({ id: payload.id, username: payload.username, role: payload.role });
    fetchProfile(token);
  }, [fetchProfile]);

  const doRefresh = useCallback(async () => {
    try {
      const r = await fetch(`${API}/auth/refresh`, { method: "POST", credentials: "include" });
      if (!r.ok) { clearSession(); return null; }
      const d = await r.json();
      if (d?.accessToken) { applyToken(d.accessToken); return d.accessToken; }
      clearSession();
      return null;
    } catch {
      clearSession();
      return null;
    }
  }, [applyToken, clearSession]);

  // Call this after linking Minecraft to update authUser without a full re-login
  const refreshProfile = useCallback(async () => {
    if (accessToken) await fetchProfile(accessToken);
  }, [accessToken, fetchProfile]);

  useEffect(() => {
    doRefresh().finally(() => setLoading(false));
  }, [doRefresh]);

  useEffect(() => {
    if (!authUser) return;
    refreshTimer.current = setInterval(doRefresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(refreshTimer.current);
  }, [authUser, doRefresh]);

  const login = async ({ email, password }) => {
    const res  = await fetch(`${API}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.errors?.[0]?.msg || data.error || "Login failed.");
    applyToken(data.accessToken);
    return data.user;
  };

  const register = async ({ username, email, password }) => {
    const res  = await fetch(`${API}/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ username, email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.errors?.[0]?.msg || data.error || "Registration failed.");
    applyToken(data.accessToken);
    return data.user;
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }).catch(() => {});
    clearSession();
  };

  return (
    <AuthContext.Provider value={{ authUser, accessToken, loading, login, register, logout, doRefresh, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}